/**
 * @fileoverview Memory Game Index Screen
 * @description Game setup where users choose difficulty, image set, and play mode
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Image,
  Alert,
  TextInput,
  FlatList,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { GameModeSelector } from "@/modules/games";
import {
  ImageSetSelector,
  GridSize,
  ImageSetType,
  DIFFICULTY_CONFIGS,
} from "@/modules/memory";

type SessionMode = "free" | "weekend" | "practice";
type PlayType = "solo" | "versus";

interface FamilyMember {
  id: string;
  full_name: string;
  avatar_url: string | null;
  team_id?: string;
}

/**
 * MemoryIndexScreen - Game setup
 * Uses activeProfile to support guardian mode - when a guardian switches to
 * a dependent, games are created for that dependent.
 */
export default function MemoryIndexScreen() {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { user, activeProfile, activeWeekend, userTeam } = useAuth();
  const { mode: urlMode } = useLocalSearchParams<{ mode?: string }>();
  
  // Use activeProfile for game actions (supports guardian mode)
  const targetUserId = activeProfile?.id;
  const targetProfile = activeProfile;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<SessionMode>(
    urlMode === "weekend" ? "weekend" : "free"
  );
  const [playType, setPlayType] = useState<PlayType>("solo");
  const [gridSize, setGridSize] = useState<GridSize>("4x4");
  const [imageSetType, setImageSetType] = useState<ImageSetType>("random");
  const [familyImages, setFamilyImages] = useState<string[]>([]);
  const [gameEnabledForWeekend, setGameEnabledForWeekend] = useState(false);
  const [stats, setStats] = useState({
    gamesPlayed: 0,
    bestTime: null as number | null,
    perfectGames: 0,
    gamesWon: 0,
    lastDifficulty: null as GridSize | null,
    difficultyStats: {} as Record<string, { games_played: number; best_time_ms: number | null; wins: number }>,
  });
  const [weekendStats, setWeekendStats] = useState({
    gamesPlayed: 0,
    bestTime: null as number | null,
    perfectGames: 0,
    gamesWon: 0,
    difficultyStats: {} as Record<string, { games_played: number; best_time_ms: number | null; wins: number }>,
  });
  
  // Challenge state
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter family members by search query
  const filteredMembers = familyMembers.filter((member) =>
    member.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Determine if weekend mode is available
  const isWeekendModeAvailable = !!activeWeekend && !!userTeam && gameEnabledForWeekend;
  const weekendUnavailableReason = !activeWeekend
    ? "Geen actief weekend"
    : !userTeam
    ? "Je zit nog niet in een team"
    : !gameEnabledForWeekend
    ? "Niet geactiveerd voor dit weekend"
    : "";

  /**
   * Fetch weekend game config to check if game is enabled
   */
  const fetchWeekendGameConfig = useCallback(async () => {
    if (!activeWeekend) {
      setGameEnabledForWeekend(false);
      return;
    }

    try {
      const { data } = await supabase
        .from("weekend_game_config")
        .select("is_enabled")
        .eq("weekend_id", activeWeekend.id)
        .eq("game_slug", "puzzle_matching")
        .single();

      setGameEnabledForWeekend(data?.is_enabled ?? false);
    } catch {
      setGameEnabledForWeekend(false);
    }
  }, [activeWeekend]);

  /**
   * Fetch family images for weekend
   */
  const fetchFamilyImages = useCallback(async () => {
    try {
      const { data } = await supabase
        .from("memory_image_sets")
        .select("images")
        .eq("type", "family")
        .eq("is_active", true)
        .single();

      if (data?.images) {
        setFamilyImages(data.images as string[]);
      }
    } catch (err) {
      // No family images available
    }
  }, []);

  /**
   * Fetch user stats (both free play and weekend) for active profile
   */
  const fetchStats = useCallback(async () => {
    if (!targetUserId) return;

    try {
      // Fetch free play stats
      const { data: freeData } = await supabase
        .from("user_game_stats")
        .select("games_played, games_won, personal_best_time_ms, stats")
        .eq("user_id", targetUserId)
        .eq("game_slug", "puzzle_matching")
        .eq("mode", "free")
        .single();

      if (freeData) {
        const statsData = freeData.stats as { 
          perfect_games?: number; 
          last_difficulty?: GridSize;
          difficulty_stats?: Record<string, { games_played: number; best_time_ms: number | null; wins: number }>;
        } | null;
        
        setStats({
          gamesPlayed: freeData.games_played ?? 0,
          bestTime: freeData.personal_best_time_ms ? Math.floor(freeData.personal_best_time_ms / 1000) : null,
          perfectGames: statsData?.perfect_games ?? 0,
          gamesWon: freeData.games_won ?? 0,
          lastDifficulty: statsData?.last_difficulty ?? null,
          difficultyStats: statsData?.difficulty_stats ?? {},
        });
      }

      // Fetch weekend stats if active weekend
      if (activeWeekend) {
        const { data: weekendData } = await supabase
          .from("user_game_stats")
          .select("games_played, games_won, personal_best_time_ms, stats")
          .eq("user_id", targetUserId)
          .eq("game_slug", "puzzle_matching")
          .eq("mode", "weekend")
          .single();

        if (weekendData) {
          const statsData = weekendData.stats as { 
            perfect_games?: number;
            difficulty_stats?: Record<string, { games_played: number; best_time_ms: number | null; wins: number }>;
          } | null;
          
          setWeekendStats({
            gamesPlayed: weekendData.games_played ?? 0,
            bestTime: weekendData.personal_best_time_ms ? Math.floor(weekendData.personal_best_time_ms / 1000) : null,
            perfectGames: statsData?.perfect_games ?? 0,
            gamesWon: weekendData.games_won ?? 0,
            difficultyStats: statsData?.difficulty_stats ?? {},
          });
        }
      }
    } catch (err) {
      // No stats yet
    }
  }, [targetUserId, activeWeekend]);

  /**
   * Fetch family members for challenges (for active profile)
   */
  const fetchFamilyMembers = useCallback(async () => {
    if (!targetUserId) return;
    setIsLoadingMembers(true);

    try {
      // Get all family members (excluding active profile)
      const { data: members, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .neq("id", targetUserId)
        .order("full_name");

      if (error) throw error;

      // For weekend mode, get team memberships via teams table
      let membersWithTeams: FamilyMember[] = members || [];
      
      if (selectedMode === "weekend" && activeWeekend) {
        // Join team_members with teams to filter by weekend_id
        const { data: teamMembers } = await supabase
          .from("team_members")
          .select("user_id, team_id, teams!inner(weekend_id)")
          .eq("teams.weekend_id", activeWeekend.id);

        if (teamMembers) {
          membersWithTeams = members?.map((m) => ({
            ...m,
            team_id: teamMembers.find((tm) => tm.user_id === m.id)?.team_id,
          })) || [];

          // For weekend mode, filter to only show users from different teams
          if (userTeam?.id) {
            membersWithTeams = membersWithTeams.filter(
              (m) => m.team_id && m.team_id !== userTeam.id
            );
          }
        }
      }

      setFamilyMembers(membersWithTeams);
    } catch (err) {
      console.error("Error fetching family members:", err);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [targetUserId, selectedMode, activeWeekend, userTeam?.id]);

  useEffect(() => {
    fetchFamilyImages();
    fetchStats();
    fetchWeekendGameConfig();
  }, [fetchFamilyImages, fetchStats, fetchWeekendGameConfig]);

  useEffect(() => {
    if (showChallengeModal) {
      fetchFamilyMembers();
    }
  }, [showChallengeModal, fetchFamilyMembers]);

  /**
   * Start a solo game (for active profile)
   */
  const handleStartSoloGame = async () => {
    if (!targetUserId) return;

    setIsLoading(true);

    try {
      const { data: session, error } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "puzzle_matching",
          created_by: targetUserId,
          participants: [{ user_id: targetUserId, role: "player", name: targetProfile?.full_name ?? "Speler" }],
          mode: selectedMode,
          session_type: "solo",
          status: "active",
          game_state: {
            grid_size: gridSize,
            image_set_type: imageSetType,
          },
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        })
        .select()
        .single();

      if (error) throw error;

      router.push(`/(tabs)/games/memory/${session.id}` as any);
    } catch (err) {
      console.error("Error starting game:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Challenge another player to a time race (for active profile)
   */
  const handleChallengeUser = async (opponent: FamilyMember) => {
    if (!targetUserId || !targetProfile) return;

    setShowChallengeModal(false);
    setIsLoading(true);

    try {
      // Generate a seed for consistent card shuffling
      const shuffleSeed = Math.floor(Math.random() * 1000000);

      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "puzzle_matching",
          created_by: targetUserId,
          participants: [
            { user_id: targetUserId, role: "player", name: targetProfile.full_name },
            { user_id: opponent.id, role: "player", name: opponent.full_name },
          ],
          mode: selectedMode,
          session_type: "versus",
          status: "waiting",
          game_state: {
            grid_size: gridSize,
            image_set_type: imageSetType,
            shuffle_seed: shuffleSeed, // Same seed = same card layout
            challenged_user_id: opponent.id,
            challenger_name: targetProfile.full_name,
            challenger_id: targetUserId,
            opponent_name: opponent.full_name,
            opponent_id: opponent.id,
            race_type: "time", // Time race mode
            player_times: {}, // Track completion times
            player_ready: {}, // Track ready status
          },
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        })
        .select()
        .single();

      if (sessionError) throw sessionError;

      // Send notification to challenged user
      await supabase.from("notifications").insert({
        user_id: opponent.id,
        type: "game_challenge",
        title: "Memory Uitdaging!",
        body: `${targetProfile.full_name} daagt je uit voor een Memory tijd-race!`,
        action_url: `/(tabs)/games/memory/${session.id}`,
        action_data: {
          sessionId: session.id,
          gameSlug: "puzzle_matching",
          challengerId: targetUserId,
          challengedId: opponent.id,
          action_url: `/(tabs)/games/memory/${session.id}`,
        },
        sender_id: targetUserId,
        weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        notification_type: "game_challenge",
        lottie_animation: "muscle_emoji",
        priority: "high",
      });

      router.push(`/(tabs)/games/memory/${session.id}` as any);
    } catch (err) {
      console.error("Error challenging user:", err);
      Alert.alert("Fout", "Kon uitdaging niet versturen.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle start button press
   */
  const handleStartGame = () => {
    if (playType === "solo") {
      handleStartSoloGame();
    } else {
      setSearchQuery("");
      setShowChallengeModal(true);
    }
  };

  /**
   * Format time
   */
  const formatTime = (seconds: number | null): string => {
    if (seconds === null) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={styles.backButton}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={[styles.title, { color: colors.text }]}>
            🧩 Memory
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Vind alle paren zo snel mogelijk!
          </Text>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* Game Mode */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Spelmodus
          </Text>
          <GameModeSelector
            mode={selectedMode}
            onModeChange={setSelectedMode}
            weekendAvailable={isWeekendModeAvailable}
            weekendUnavailableReason={weekendUnavailableReason}
            isDark={isDark}
            compact
          />
        </View>

        {/* Play Type (Solo vs Versus) */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Hoe wil je spelen?
          </Text>
          <View style={styles.playTypeContainer}>
            <TouchableOpacity
              style={[
                styles.playTypeCard,
                { backgroundColor: colors.surface },
                playType === "solo" && styles.playTypeCardSelected,
              ]}
              onPress={() => setPlayType("solo")}
              accessibilityLabel="Solo - speel alleen"
              accessibilityRole="radio"
              accessibilityState={{ checked: playType === "solo" }}
            >
              <Ionicons
                name="person"
                size={28}
                color={playType === "solo" ? "#8B5CF6" : colors.textSecondary}
              />
              <Text
                style={[
                  styles.playTypeTitle,
                  { color: playType === "solo" ? "#8B5CF6" : colors.text },
                ]}
              >
                Solo
              </Text>
              <Text
                style={[styles.playTypeDesc, { color: colors.textSecondary }]}
              >
                Speel alleen en verbeter je tijd
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.playTypeCard,
                { backgroundColor: colors.surface },
                playType === "versus" && styles.playTypeCardSelectedVersus,
              ]}
              onPress={() => setPlayType("versus")}
              accessibilityLabel="Versus - daag iemand uit"
              accessibilityRole="radio"
              accessibilityState={{ checked: playType === "versus" }}
            >
              <Ionicons
                name="people"
                size={28}
                color={playType === "versus" ? "#F59E0B" : colors.textSecondary}
              />
              <Text
                style={[
                  styles.playTypeTitle,
                  { color: playType === "versus" ? "#F59E0B" : colors.text },
                ]}
              >
                Tijd Race
              </Text>
              <Text
                style={[styles.playTypeDesc, { color: colors.textSecondary }]}
              >
                Race tegen een familielid!
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Difficulty Selection */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Moeilijkheid
          </Text>
          <View style={styles.difficultyGrid}>
            {(Object.keys(DIFFICULTY_CONFIGS) as GridSize[]).map((size) => {
              const config = DIFFICULTY_CONFIGS[size];
              const isSelected = gridSize === size;
              return (
                <TouchableOpacity
                  key={size}
                  style={[
                    styles.difficultyCard,
                    { backgroundColor: colors.surface },
                    isSelected && styles.difficultyCardSelected,
                  ]}
                  onPress={() => setGridSize(size)}
                  accessibilityLabel={`${config.label} - ${config.pairs} paren`}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: isSelected }}
                >
                  <Text
                    style={[
                      styles.difficultyEmoji,
                      isSelected && styles.difficultyEmojiSelected,
                    ]}
                  >
                    {size === "3x4"
                      ? "🌱"
                      : size === "4x4"
                      ? "🌿"
                      : size === "4x5"
                      ? "🌳"
                      : "🏔️"}
                  </Text>
                  <Text
                    style={[
                      styles.difficultyLabel,
                      { color: colors.text },
                      isSelected && styles.difficultyLabelSelected,
                    ]}
                  >
                    {config.label}
                  </Text>
                  <Text
                    style={[
                      styles.difficultyPairs,
                      { color: colors.textSecondary },
                    ]}
                  >
                    {config.pairs} paren
                  </Text>
                  {isSelected && (
                    <View style={styles.selectedCheck}>
                      <Ionicons
                        name="checkmark-circle"
                        size={20}
                        color="#22C55E"
                      />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Image Set Selection */}
        <View style={styles.section}>
          <ImageSetSelector
            selectedType={imageSetType}
            onSelectType={setImageSetType}
            familyImagesAvailable={familyImages.length >= 6}
            familyImageCount={familyImages.length}
          />
        </View>

        {/* Stats */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {selectedMode === "weekend" ? "Weekend Statistieken" : "Jouw Statistieken"}
          </Text>
          <View style={[styles.statsCard, { backgroundColor: colors.surface }]}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {selectedMode === "weekend" ? weekendStats.gamesPlayed : stats.gamesPlayed}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gespeeld
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#3B82F6" }]}>
                {formatTime(selectedMode === "weekend" ? weekendStats.bestTime : stats.bestTime)}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Beste tijd
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#F59E0B" }]}>
                {selectedMode === "weekend" ? weekendStats.perfectGames : stats.perfectGames}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Perfect 🏆
              </Text>
            </View>
          </View>

          {/* Difficulty-specific stats for selected difficulty */}
          {(() => {
            const currentStats = selectedMode === "weekend" ? weekendStats : stats;
            const diffStats = currentStats.difficultyStats[gridSize];
            if (!diffStats || diffStats.games_played === 0) return null;

            return (
              <View style={[styles.difficultyStatsCard, { backgroundColor: colors.surfaceSecondary }]}>
                <View style={styles.difficultyStatsHeader}>
                  <Text style={[styles.difficultyStatsTitle, { color: colors.textSecondary }]}>
                    {DIFFICULTY_CONFIGS[gridSize].label} statistieken
                  </Text>
                </View>
                <View style={styles.difficultyStatsRow}>
                  <View style={styles.difficultyStatItem}>
                    <Text style={[styles.difficultyStatValue, { color: colors.text }]}>
                      {diffStats.games_played}
                    </Text>
                    <Text style={[styles.difficultyStatLabel, { color: colors.textTertiary }]}>
                      gespeeld
                    </Text>
                  </View>
                  {diffStats.best_time_ms && (
                    <View style={styles.difficultyStatItem}>
                      <Text style={[styles.difficultyStatValue, { color: "#22C55E" }]}>
                        {formatTime(Math.floor(diffStats.best_time_ms / 1000))}
                      </Text>
                      <Text style={[styles.difficultyStatLabel, { color: colors.textTertiary }]}>
                        record
                      </Text>
                    </View>
                  )}
                  {playType === "versus" && (
                    <View style={styles.difficultyStatItem}>
                      <Text style={[styles.difficultyStatValue, { color: "#8B5CF6" }]}>
                        {diffStats.wins}
                      </Text>
                      <Text style={[styles.difficultyStatLabel, { color: colors.textTertiary }]}>
                        gewonnen
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })()}
        </View>
      </ScrollView>

      {/* Start Button */}
      <View style={styles.bottomContainer}>
        <TouchableOpacity
          style={[
            styles.startButton,
            playType === "versus" && styles.startButtonVersus,
          ]}
          onPress={handleStartGame}
          disabled={isLoading}
          accessibilityLabel={playType === "solo" ? "Start spel" : "Daag iemand uit"}
          accessibilityRole="button"
        >
          {isLoading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <>
              <Ionicons
                name={playType === "solo" ? "play" : "flash"}
                size={24}
                color="#FFFFFF"
              />
              <Text style={styles.startButtonText}>
                {playType === "solo" ? "Start Spel" : "Daag Iemand Uit"}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* Challenge Modal */}
      <Modal
        visible={showChallengeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowChallengeModal(false)}
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={0}
        >
          <View style={styles.modalBackdrop}>
            <View style={[styles.modalContainer, { backgroundColor: colors.surface }]}>
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, { color: colors.text }]}>
                Daag Iemand Uit
              </Text>
              <TouchableOpacity
                onPress={() => setShowChallengeModal(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.modalSubtitle, { color: colors.textSecondary }]}>
              {selectedMode === "weekend"
                ? "Daag een tegenstander van een ander team uit!"
                : "Race tegen een familielid - wie vindt alle paren het snelst?"}
            </Text>

            {/* Search bar */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textSecondary} />
              <TextInput
                style={[styles.searchInput, { color: colors.text }]}
                placeholder="Zoek op naam..."
                placeholderTextColor={colors.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery("")}>
                  <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </View>
            
            {selectedMode === "weekend" && (
              <View style={styles.weekendNotice}>
                <Ionicons name="trophy" size={14} color="#F59E0B" />
                <Text style={styles.weekendNoticeText}>
                  Weekend modus: alleen tegenstanders van andere teams
                </Text>
              </View>
            )}

            <ScrollView style={styles.membersList} showsVerticalScrollIndicator={false}>
              {isLoadingMembers ? (
                <ActivityIndicator color={colors.primary} style={{ marginTop: 20 }} />
              ) : filteredMembers.length === 0 ? (
                <View style={styles.emptyMembers}>
                  <Ionicons name="people-outline" size={48} color={colors.textTertiary} />
                  <Text style={[styles.emptyMembersText, { color: colors.textSecondary }]}>
                    {searchQuery
                      ? "Geen resultaten gevonden"
                      : selectedMode === "weekend"
                      ? "Geen tegenstanders gevonden in andere teams"
                      : "Geen familieleden gevonden"}
                  </Text>
                </View>
              ) : (
                filteredMembers.map((member) => (
                  <TouchableOpacity
                    key={member.id}
                    style={[styles.memberCard, { backgroundColor: colors.surfaceSecondary }]}
                    onPress={() => handleChallengeUser(member)}
                    accessibilityLabel={`Daag ${member.full_name} uit`}
                    accessibilityRole="button"
                  >
                    {member.avatar_url ? (
                      <Image
                        source={{ uri: member.avatar_url }}
                        style={styles.memberAvatar}
                      />
                    ) : (
                      <View style={[styles.memberAvatarPlaceholder, { backgroundColor: colors.primaryLight }]}>
                        <Text style={[styles.memberAvatarText, { color: colors.primary }]}>
                          {member.full_name.charAt(0).toUpperCase()}
                        </Text>
                      </View>
                    )}
                    <View style={styles.memberInfo}>
                      <Text style={[styles.memberName, { color: colors.text }]}>
                        {member.full_name}
                      </Text>
                    </View>
                    <Ionicons name="flash" size={24} color="#F59E0B" />
                  </TouchableOpacity>
                ))
              )}
              </ScrollView>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 20,
    paddingBottom: 100,
  },
  section: {
    gap: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  difficultyGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  difficultyCard: {
    width: "48%",
    padding: 16,
    borderRadius: 16,
    alignItems: "center",
    borderWidth: 2,
    borderColor: "transparent",
    position: "relative",
  },
  difficultyCardSelected: {
    borderColor: "#8B5CF6",
  },
  difficultyEmoji: {
    fontSize: 28,
    marginBottom: 6,
  },
  difficultyEmojiSelected: {
    transform: [{ scale: 1.1 }],
  },
  difficultyLabel: {
    fontSize: 15,
    fontWeight: "600",
  },
  difficultyLabelSelected: {
    color: "#8B5CF6",
  },
  difficultyPairs: {
    fontSize: 12,
    marginTop: 2,
  },
  selectedCheck: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  statsCard: {
    flexDirection: "row",
    borderRadius: 16,
    padding: 16,
    justifyContent: "space-around",
  },
  statItem: {
    alignItems: "center",
    flex: 1,
  },
  statValue: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  statLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  bottomContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    paddingBottom: 32,
    backgroundColor: "rgba(15, 23, 42, 0.95)",
    borderTopWidth: 1,
    borderTopColor: "rgba(255, 255, 255, 0.1)",
  },
  startButton: {
    backgroundColor: "#8B5CF6",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 16,
    borderRadius: 16,
  },
  startButtonVersus: {
    backgroundColor: "#F59E0B",
  },
  startButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  // Play type styles
  playTypeContainer: {
    flexDirection: "row",
    gap: 12,
  },
  playTypeCard: {
    flex: 1,
    padding: 16,
    borderRadius: 16,
    alignItems: "center",
    borderWidth: 2,
    borderColor: "transparent",
    gap: 6,
  },
  playTypeCardSelected: {
    borderColor: "#8B5CF6",
    backgroundColor: "rgba(139, 92, 246, 0.1)",
  },
  playTypeCardSelectedVersus: {
    borderColor: "#F59E0B",
    backgroundColor: "rgba(245, 158, 11, 0.1)",
  },
  playTypeTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  playTypeDesc: {
    fontSize: 11,
    textAlign: "center",
  },
  // Modal styles
  keyboardAvoidingView: {
    flex: 1,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "flex-end",
  },
  modalContainer: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    maxHeight: "70%",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "700",
  },
  modalCloseButton: {
    padding: 4,
  },
  modalSubtitle: {
    fontSize: 14,
    marginBottom: 12,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(128, 128, 128, 0.1)",
    borderRadius: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
  },
  weekendNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 16,
  },
  weekendNoticeText: {
    fontSize: 12,
    color: "#F59E0B",
    fontWeight: "500",
  },
  membersList: {
    maxHeight: 400,
  },
  memberCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
  },
  memberAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    marginRight: 12,
  },
  memberAvatarPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 22,
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  memberAvatarText: {
    fontSize: 18,
    fontWeight: "700",
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: 16,
    fontWeight: "500",
  },
  emptyMembers: {
    alignItems: "center",
    padding: 32,
    gap: 12,
  },
  emptyMembersText: {
    fontSize: 14,
    textAlign: "center",
  },
  // Difficulty stats styles
  difficultyStatsCard: {
    marginTop: 12,
    borderRadius: 12,
    padding: 12,
  },
  difficultyStatsHeader: {
    marginBottom: 10,
  },
  difficultyStatsTitle: {
    fontSize: 12,
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  difficultyStatsRow: {
    flexDirection: "row",
    justifyContent: "space-around",
  },
  difficultyStatItem: {
    alignItems: "center",
  },
  difficultyStatValue: {
    fontSize: 18,
    fontWeight: "700",
  },
  difficultyStatLabel: {
    fontSize: 10,
    marginTop: 2,
  },
});

